import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import { fetchConsentConfig } from '../../../actions/marketing-consent-actions';
import {
  baseLoginCheckUsernameV2,
  checkUsernameCaptchaFailureV2,
  getPasswordlessFromUserAction,
  requestCheckUsernameV2ActionSelected,
  requestCheckUsernameV2SelectedActionContinued,
  resetLoginV2,
} from '../../../actions/login-v2-actions';
import type { UserAction } from '../../../actions/login-actions';
import { setRememberMeCheckbox, showFormErrors } from '../../../actions/login-actions';
import { recaptchaCheckUsernameFetchTokenInteraction } from '../../../browser-metrics/constants';
import { ChallengeResponse, useRecaptchaContext } from '../../../contexts/recaptcha';
import {
  selectLoginV2CanSubmitPassword,
  selectLoginV2IsLoading,
} from '../../../selectors/login-v2-selectors';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import {
  CheckUserNamePasswordlessType,
  LoginFieldError,
  OidcDisplay,
  URLParameters,
} from '../../../types';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { isMobileOidc, isOidcDisplayMode } from '../../../utilities/oidc/oidc';
import { loginPageId } from '../constants';
import {
  useCobrandingApplication,
  useEmailFromSearchParams,
  useLoginRecoveryOptions,
  useSearchParamCodes,
} from './utility';
import { useRegisterComponent } from '../../orchestrator';
import { buildLoginErrorMessage } from './errorMessages';
import { useSignupLink } from './useSignupLink';
import { useRememberMe } from './useRememberMe';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';
import { useOpenAwsCaptchaModalCallback } from '../../../aws-captcha/aws-captcha.hooks';
import type { LoginGlobalErrorWithMessage } from './useLogin';

/**
 * This hook is analogous to `useLogin`, except that it targets the `state.loginV2` redux slice
 * instead of the old `state.login` slice.
 *
 * The duplication (of the slice and hooks) is intended to enable simple and backwards-compatible
 * migration of login page implementations to use the `/v2/check-username` endpoint.
 *
 * See README-login-v2.md for details.
 * */
export const useBaseLoginV2Model = ({ autoCheckUsername = true } = {}) => {
  // Registering under both names so advanced cobranding framework recognises it.
  useRegisterComponent(useBaseLoginV2Model.name);
  useRegisterComponent('useLogin');

  const analyticsClient = useAnalyticsClient();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();
  const loginRecoveryOptions = useLoginRecoveryOptions();
  const onShowAwsCaptchaModal = useOpenAwsCaptchaModalCallback();

  const queryParams: URLParameters = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search]
  );

  const { rememberMeEnabled, rememberedAccounts, isRememberMeChecked, onToggleRememberMeCheckbox } =
    useRememberMe({ loginStateSource: 'v2' });

  const loginV2 = useAppSelector(state => state.loginV2);
  const canSubmitPassword = useAppSelector(selectLoginV2CanSubmitPassword);
  const isLoading = useAppSelector(selectLoginV2IsLoading);
  const destinationOrgId = useAppSelector(state => state.orgLoginConfig.organisationId);
  const orgLoginConfigSaml = useAppSelector(state => state.orgLoginConfig.samlConnectionId);
  const orgLoginConfigAutoRedirect = useAppSelector(state => state.orgLoginConfig.autoRedirect);
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const isMobileApp = useAppSelector(state => state.microbranding.isMobileApp);

  const { errorCode, infoCode } = useSearchParamCodes();
  const combinedError = useMemo(() => loginV2.error || errorCode, [loginV2.error, errorCode]);

  const { formatMessage } = useIntl();
  const signupLink = useSignupLink();
  const application = useCobrandingApplication();

  const combinedErrorWithMessage: LoginGlobalErrorWithMessage | undefined = useMemo(() => {
    if (!combinedError) {
      return;
    }

    return {
      ...combinedError,
      message: buildLoginErrorMessage({
        error: combinedError,
        formatMessage,
        signupLink,
        nextAttempt: loginV2.nextAttempt,
        application,
      }),
    };
  }, [application, combinedError, formatMessage, loginV2.nextAttempt, signupLink]);

  useEffect(() => {
    const shouldRedirect =
      !isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect);

    if (loginV2.redirectUrl) {
      navigate(loginV2.redirectUrl);
    } else if (
      loginV2.isUsernameChecked &&
      loginV2.selectedUserAction?.action === 'signup' &&
      shouldRedirect
    ) {
      dispatch(resetLoginV2());
      navigate(
        location.search.length === 0
          ? '/signup?redirectedFrom=login'
          : `/signup${location.search}&redirectedFrom=login`
      );
    }
  }, [dispatch, isMobileApp, location.search, loginV2, navigate, oidcContext]);

  const [isCheckUsernameCaptchaLoading, setIsCheckUsernameCaptchaLoading] = useState(false);
  const { isLoaded: recaptchaIsLoaded, recaptchaType, fetchToken } = useRecaptchaContext();
  const isRestrictedEmail = loginV2.restrictedEmail || queryParams?.restrict === 'true';

  const fetchRecaptchaToken = useCallback(async () => {
    try {
      setIsCheckUsernameCaptchaLoading(true);
      recaptchaCheckUsernameFetchTokenInteraction.start();
      const challengeResponse = await fetchToken();
      recaptchaCheckUsernameFetchTokenInteraction.stop({
        customData: { restrict: isRestrictedEmail },
      });
      return challengeResponse;
    } catch (error) {
      analyticsClient.errorShownEvent(loginPageId, 'recaptchaError', {
        errorMessage: error.message,
      });
      dispatch(checkUsernameCaptchaFailureV2(navigator.onLine));
      return null;
    } finally {
      setIsCheckUsernameCaptchaLoading(false);
    }
  }, [analyticsClient, dispatch, fetchToken, isRestrictedEmail]);

  const buildCheckUsernameParams = useCallback(
    async (username: string, rememberOverride?: boolean) => {
      let challengeResponse: ChallengeResponse | null = null;
      const remember = rememberOverride ?? isRememberMeChecked;

      const checkUsernameParams = new URLSearchParams(location.search);
      if (remember) {
        checkUsernameParams.append('remember', 'true');
      }

      if (recaptchaIsLoaded) {
        challengeResponse = await fetchRecaptchaToken();
      }

      return {
        analyticsClient,
        username,
        params: `?${checkUsernameParams.toString()}`,
        recoveryToken: loginRecoveryOptions(username)?.recoveryToken,
        challengeResponse,
        destinationOrgId,
        onShowAwsCaptchaModal,
      };
    },
    [
      analyticsClient,
      destinationOrgId,
      fetchRecaptchaToken,
      isRememberMeChecked,
      location.search,
      loginRecoveryOptions,
      onShowAwsCaptchaModal,
      recaptchaIsLoaded,
    ]
  );

  const checkUsername = useCallback(
    async (username: string, rememberOverride?: boolean) => {
      const remember = rememberOverride ?? isRememberMeChecked;
      const query = {
        ...queryParams,
        email: username,
      };

      if (remember) {
        dispatch(setRememberMeCheckbox(true));
      }

      delete query.errorCode;
      const searchParams = new URLSearchParams(query);
      navigate(`/login?${searchParams.toString()}${location.hash}`);

      const usernameCheckParams = await buildCheckUsernameParams(username, rememberOverride);

      analyticsClient.formSubmittedEvent(loginPageId, 'checkUsernameV2', {
        recaptchaType: usernameCheckParams.challengeResponse?.type,
        rememberMeChecked: !!remember,
      });

      dispatch(baseLoginCheckUsernameV2(usernameCheckParams));
      dispatch(fetchConsentConfig(username));
    },
    [
      analyticsClient,
      buildCheckUsernameParams,
      dispatch,
      isRememberMeChecked,
      location.hash,
      navigate,
      queryParams,
    ]
  );

  const onSelectCheckUsernameUserAction = useCallback(
    (userAction: UserAction) => {
      dispatch(requestCheckUsernameV2ActionSelected(userAction));
    },
    [dispatch]
  );

  const onContinueCheckUsernameUserAction = useCallback(
    async (username: string, userAction: UserAction) => {
      const usernameCheckParams = await buildCheckUsernameParams(username);
      dispatch(requestCheckUsernameV2SelectedActionContinued(usernameCheckParams, userAction));
    },
    [buildCheckUsernameParams, dispatch]
  );

  const emailFromSearchParams = useEmailFromSearchParams();

  useEffect(() => {
    const handleMount = async () => {
      if (!recaptchaIsLoaded || !autoCheckUsername || !isRestrictedEmail) {
        return;
      }

      const emailWithDefault = emailFromSearchParams || '';
      if (emailWithDefault && recaptchaType === 'score') {
        await checkUsername(emailWithDefault);
      }
    };

    void handleMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaIsLoaded]);

  const resetLogin = useCallback(() => dispatch(resetLoginV2()), [dispatch]);

  const passwordless = getPasswordlessFromUserAction(loginV2.selectedUserAction);
  const isPasswordlessAllowed =
    passwordless !== CheckUserNamePasswordlessType.Disallowed && !isMobileOidc(oidcContext);

  const loginPrompt = queryParams?.prompt;
  const isCheckUsernameLoading = loginV2.isCheckingUsername || isCheckUsernameCaptchaLoading;
  const shouldShowRememberedAccounts =
    rememberMeEnabled &&
    emailFromSearchParams === '' &&
    !isCheckUsernameLoading &&
    !['true', 'login'].includes(loginPrompt || '') &&
    !(orgLoginConfigSaml && orgLoginConfigAutoRedirect) &&
    rememberedAccounts &&
    rememberedAccounts.length > 0;

  const onValidationError = useCallback(
    (errors: LoginFieldError) => dispatch(showFormErrors(errors)),
    [dispatch]
  );

  return {
    isPasswordlessAllowed,
    isUsernameChecked: loginV2.isUsernameChecked,
    canSubmitPassword,
    isCheckUsernameLoading,
    checkUsernameError: loginV2.fieldErrors?.email,
    isRestrictedEmail,
    checkUsername,
    loginPrompt,
    error: combinedErrorWithMessage,
    infoCode,
    nextAttemptCount: loginV2.nextAttempt,
    redirectUrl: loginV2.redirectUrl,
    redirectType: loginV2.redirectType,
    resetLogin,
    rememberMeEnabled,
    rememberedAccounts,
    shouldShowRememberedAccounts,
    isRememberMeChecked,
    onToggleRememberMeCheckbox,
    checkUsernameUserActions: loginV2.userActions,
    selectedCheckUsernameUserAction: loginV2.selectedUserAction,
    onSelectCheckUsernameUserAction,
    onContinueCheckUsernameUserAction,
    onValidationError,
    isLoading,
  };
};
